import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Archive } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable Archive button — soft-hides a record via the module's DELETE endpoint.
 * Now prompts for a reason before archiving.
 */
export function ArchiveButton({ endpoint, label, onArchived, testId, size = "sm" }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  async function doArchive() {
    if (!reason.trim()) {
      toast.error("Please provide a reason before archiving");
      return;
    }
    setLoading(true);
    try {
      await api.delete(endpoint, { params: { reason: reason.trim() } });
      toast.success(`${label} archived`);
      setOpen(false);
      setReason("");
      if (onArchived) onArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    } finally { setLoading(false); }
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setReason(""); }}>
      <AlertDialogTrigger asChild>
        <Button size={size} variant="outline" className="border-amber-200 text-amber-800 hover:bg-amber-50" data-testid={testId}>
          <Archive size={14} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive “{label}”?</AlertDialogTitle>
          <AlertDialogDescription>
            The record will be hidden from the main list but stays in the database and can be restored from the Archive at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5 py-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason for archiving <span className="text-rose-600">*</span></Label>
          <Textarea
            rows={3}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Duplicate record, contract ended, replaced by newer version…"
            className="border-border"
            data-testid={`${testId}-reason`}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); doArchive(); }} disabled={loading || !reason.trim()} data-testid={`${testId}-confirm`}>
            {loading ? "Archiving…" : "Archive"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
